import {
  DEFAULT_APPEARANCE,
  ELEMENT_INFO,
  FACES,
  GENDERS,
  HAIR_COLORS,
  HAIRSTYLES,
  OUTFIT_COLORS,
  OUTFIT_STYLES,
  SECTS,
  SKIN_TONES,
  SKILLS,
  type Appearance,
  type SectId,
} from '@jianghu/game-data';
import { CharacterNameSchema, type CreateCharacterBody } from '@jianghu/protocol';
import { useMemo, useState } from 'react';
import { CharacterPreview } from '../components/CharacterPreview';
import { audio } from '../game/audio/audio';
import { api, ApiError } from '../services/api';
import { useSession } from '../stores/session-store';

const label = (s: string): string => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function Options<T extends string>({
  values,
  value,
  onChange,
  name,
}: {
  values: readonly T[];
  value: T;
  onChange: (v: T) => void;
  name: string;
}) {
  return (
    <div className="chip-row" role="radiogroup" aria-label={name}>
      {values.map((v) => (
        <button
          key={v}
          type="button"
          className="chip"
          aria-pressed={value === v}
          role="radio"
          aria-checked={value === v}
          onClick={() => onChange(v)}
        >
          {label(v)}
        </button>
      ))}
    </div>
  );
}

function Swatches({
  values,
  value,
  onChange,
  name,
}: {
  values: readonly string[];
  value: string;
  onChange: (v: string) => void;
  name: string;
}) {
  return (
    <div className="chip-row" role="radiogroup" aria-label={name}>
      {values.map((v) => (
        <button
          key={v}
          type="button"
          className="swatch"
          style={{ background: v }}
          aria-pressed={value === v}
          role="radio"
          aria-checked={value === v}
          aria-label={`${name} ${v}`}
          onClick={() => onChange(v)}
        />
      ))}
    </div>
  );
}

export function CharacterCreateScreen() {
  const realm = useSession((s) => s.realm);
  const [a, setA] = useState<Appearance>({ ...DEFAULT_APPEARANCE });
  const [sect, setSect] = useState<SectId>('wudang');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof Appearance>(k: K, v: Appearance[K]): void =>
    setA((prev) => ({ ...prev, [k]: v }));
  const sectDef = SECTS[sect];
  const nameCheck = useMemo(() => CharacterNameSchema.safeParse(name), [name]);
  const sects = Object.values(SECTS).sort((x, y) => Number(y.playable) - Number(x.playable));

  const create = async (): Promise<void> => {
    if (!realm || !nameCheck.success) return;
    setBusy(true);
    setError(null);
    try {
      const c = await api.createCharacter({
        realmId: realm.id,
        name: nameCheck.data,
        sect,
        appearance: a as CreateCharacterBody['appearance'],
      });
      audio.play('quest_complete');
      useSession.getState().setCharacter(c);
      useSession.getState().go('game');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create character.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="screen" style={{ alignItems: 'flex-start' }}>
      <div className="create-layout">
        <section
          className="panel col scroll"
          style={{ maxHeight: 'calc(100vh - 48px)' }}
          aria-labelledby="look-title"
        >
          <h2 className="panel-title" id="look-title">
            Appearance
          </h2>
          <div className="field">
            <label>Body</label>
            <Options name="Gender" values={GENDERS} value={a.gender} onChange={(v) => set('gender', v)} />
          </div>
          <div className="field">
            <label>Hairstyle</label>
            <Options
              name="Hairstyle"
              values={HAIRSTYLES}
              value={a.hairstyle}
              onChange={(v) => set('hairstyle', v)}
            />
          </div>
          <div className="field">
            <label>Hair colour</label>
            <Swatches
              name="Hair colour"
              values={HAIR_COLORS}
              value={a.hairColor}
              onChange={(v) => set('hairColor', v)}
            />
          </div>
          <div className="field">
            <label>Face</label>
            <Options name="Face" values={FACES} value={a.face} onChange={(v) => set('face', v)} />
          </div>
          <div className="field">
            <label>Skin tone</label>
            <Swatches
              name="Skin tone"
              values={SKIN_TONES}
              value={a.skinTone}
              onChange={(v) => set('skinTone', v)}
            />
          </div>
          <div className="field">
            <label>Outfit</label>
            <Options
              name="Outfit"
              values={OUTFIT_STYLES}
              value={a.outfitStyle}
              onChange={(v) => set('outfitStyle', v)}
            />
          </div>
          <div className="field">
            <label>Outfit colour</label>
            <Swatches
              name="Outfit colour"
              values={OUTFIT_COLORS}
              value={a.outfitColor}
              onChange={(v) => set('outfitColor', v)}
            />
          </div>
        </section>

        <div className="col">
          <CharacterPreview
            sect={sect}
            appearance={a}
            weapon={sectDef.starterWeapon}
            armor={sectDef.starterArmor}
            caption={name || 'Nameless Wanderer'}
            subcaption={sectDef.name}
          />
          <div className="panel col">
            <div className="field" style={{ marginBottom: 4 }}>
              <label htmlFor="cname">Name</label>
              <input
                id="cname"
                className={`input ${name && !nameCheck.success ? 'invalid' : ''}`}
                value={name}
                maxLength={16}
                onChange={(e) => setName(e.target.value)}
                placeholder="3–16 letters"
              />
              {name && !nameCheck.success && (
                <span className="field-error">{nameCheck.error.issues[0]?.message}</span>
              )}
            </div>
            {error && <div className="form-error">{error}</div>}
            <div className="row">
              <button className="btn btn-ghost" onClick={() => useSession.getState().go('characters')}>
                Back
              </button>
              <span className="spacer" />
              <button
                className="btn btn-primary"
                disabled={!nameCheck.success || !sectDef.playable || busy}
                onClick={() => void create()}
              >
                {busy ? 'Creating…' : 'Begin Journey'}
              </button>
            </div>
          </div>
        </div>

        <section
          className="panel col scroll"
          style={{ maxHeight: 'calc(100vh - 48px)' }}
          aria-labelledby="sect-title"
        >
          <h2 className="panel-title" id="sect-title">
            Martial Sect
          </h2>
          <div className="sect-grid" role="radiogroup" aria-label="Sect">
            {sects.map((s) => (
              <button
                key={s.id}
                className="sect-btn"
                aria-pressed={sect === s.id}
                role="radio"
                aria-checked={sect === s.id}
                onClick={() => setSect(s.id)}
              >
                <span className="element-badge" style={{ background: ELEMENT_INFO[s.element].color }}>
                  {ELEMENT_INFO[s.element].nameZh}
                </span>
                <span>
                  <div>{s.name}</div>
                  {!s.playable && <div className="lock">Not yet open</div>}
                </span>
              </button>
            ))}
          </div>
          <div className="sect-detail">
            <h3 className="gold" style={{ fontFamily: 'var(--font-display)', margin: '12px 0 2px' }}>
              {sectDef.name} {sectDef.nameZh}
            </h3>
            <div className="muted" style={{ fontSize: 12 }}>
              {ELEMENT_INFO[sectDef.element].name} · {label(sectDef.weapon)} · {sectDef.role}
            </div>
            <p>{sectDef.description}</p>
            <p>{sectDef.style}</p>
            <div
              className="muted"
              style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}
            >
              Strengths
            </div>
            <ul>
              {sectDef.strengths.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            <div
              className="muted"
              style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}
            >
              Weaknesses
            </div>
            <ul>
              {sectDef.weaknesses.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            {sectDef.playable ? (
              <>
                <div
                  className="muted"
                  style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}
                >
                  Techniques available now
                </div>
                <ul>
                  {sectDef.skills.map((id) => (
                    <li key={id}>
                      {SKILLS[id]?.name} <span className="muted">(level {SKILLS[id]?.levelRequired})</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="form-error" style={{ marginTop: 8 }}>
                This sect’s techniques are still being forged. It will open in a later milestone.
              </p>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
